import React, { FC, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useSubscriptionStore, CardBrand, PaymentCard } from 'src/store/subscriptionStore';
import {
  useBillingProfileQuery,
  usePaymentMethodsQuery,
  useTrialEligibilityQuery,
  useSubscriptionPlansQuery,
  useValidatePromoCodeMutation,
  useCheckoutSubscriptionMutation,
  useCheckoutPreviewQuery,
  useRecordLegalAcknowledgementMutation,
  useStripeConfigQuery,
  useSyncSubscriptionMutation,
  useSetDefaultPaymentMethodMutation,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as RecommendedStarSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/trial/recommended.svg';
import { ReactComponent as CheckmarkSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/checkmark.svg';
import { ReactComponent as SecurePaymentLockSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/secure-payment-lock.svg';
import { ReactComponent as TrialIncludedSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/14-day-free-trial.svg';
import { ReactComponent as BillingInfoEditSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/reactivate-pro/billing-info-edit.svg';
import { ReactComponent as AddButtonSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/add-button.svg';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { formatMaskedCardNumber, getCardBrandDisplayName } from 'src/utils/cardUtils';
import { BillingInfoModal } from '../modals/BillingInfoModal';
import { PaymentMethodModal } from '../modals/PaymentMethodModal';
import { SelectPaymentMethodModal } from '../modals/SelectPaymentMethodModal';
import { PaymentConfirmedModal } from '../modals/PaymentConfirmedModal';
import { PaymentFailedModal } from '../modals/PaymentFailedModal';
import { toast } from 'src/utils/appAlert';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './Checkout.scss';

export const Checkout: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryFail = searchParams.get('fail');

  const {
    billingCycle,
    setStatus,
    billingInfo,
    paymentMethods,
    appliedPromo,
    applyPromoCode,
    removePromoCode,
  } = useSubscriptionStore();

  // API Queries & Mutations
  const { data: serverBillingProfile, refetch: refetchBillingProfile } = useBillingProfileQuery();
  const { data: serverPaymentMethods, refetch: refetchPaymentMethods } = usePaymentMethodsQuery();
  const { data: serverEligibility } = useTrialEligibilityQuery();
  const setDefaultPaymentMutation = useSetDefaultPaymentMethodMutation();
  const profileBillingCountry = serverBillingProfile?.country || billingInfo?.country;
  const {
    data: plansData,
    isLoading: isPlansLoading,
    isFetching: isPlansFetching,
    isError: isPlansError,
  } = useSubscriptionPlansQuery(profileBillingCountry);
  const billingCountry = profileBillingCountry || plansData?.country;
  const validatePromoMutation = useValidatePromoCodeMutation();
  const checkoutMutation = useCheckoutSubscriptionMutation();
  const syncSubscriptionMutation = useSyncSubscriptionMutation();
  const recordLegalAck = useRecordLegalAcknowledgementMutation();
  const { data: stripeConfig } = useStripeConfigQuery(true);
  const stripePromise = useMemo(
    () => (stripeConfig?.publishable_key ? loadStripe(stripeConfig.publishable_key) : null),
    [stripeConfig?.publishable_key],
  );

  const targetInterval = billingCycle === 'yearly' ? 'year' : 'month';
  const proPlan =
    plansData?.plans?.find((p) => p.tier_slug === 'pro' && p.interval === targetInterval) ||
    plansData?.plans?.find((p) => p.tier_slug === 'pro') ||
    plansData?.plans?.[0];

  const featuresList = proPlan?.features?.length ? proPlan.features : [];

  // Server-authoritative trial state
  const isTrial = serverEligibility?.trial_eligible ?? false;

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useCheckoutPreviewQuery({
    tier_slug: 'pro',
    interval: targetInterval,
    price_id: proPlan?.price_id,
    promo_code: appliedPromo?.code,
    country: billingCountry,
    trial_selection: isTrial,
    // Do not send an incomplete preview request. The plans response resolves the
    // backend-selected country when no billing profile exists yet.
    enabled: Boolean(proPlan?.price_id && billingCountry),
  });

  const isSummaryLoading =
    isPlansLoading ||
    isPlansFetching ||
    isPreviewLoading ||
    isPreviewFetching ||
    (!isPlansError && (!proPlan?.price_id || !billingCountry));

  const effectiveTrial = previewData?.is_trial ?? isTrial;
  const trialDays = previewData?.trial_days ?? serverEligibility?.trial_days ?? 0;
  const trialEndsDateFormatted = previewData?.formatted_trial_ends_at;

  // Modals
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSelectCardModalOpen, setIsSelectCardModalOpen] = useState(false);
  const [isConfirmedModalOpen, setIsConfirmedModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [failedError, setFailedError] = useState<{
    code?: string;
    subCode?: string;
    title?: string;
    message?: string;
  }>({});

  // Form states
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreedError, setAgreedError] = useState(false);

  const handleApplyPromo = () => {
    const trimmed = promoInput.trim();
    if (!trimmed || isApplyingPromo) {
      if (!trimmed) {
        setPromoError('Please enter a promo code');
      }
      return;
    }
    if (trimmed.length < 3) {
      setPromoError('Promo code must be at least 3 characters');
      return;
    }
    if (trimmed.length > 30) {
      setPromoError('Promo code cannot exceed 30 characters');
      return;
    }
    setPromoError('');
    setIsApplyingPromo(true);

    validatePromoMutation.mutate(
      {
        code: trimmed,
        tier_slug: 'pro',
        interval: targetInterval,
        price_id: proPlan?.price_id,
        country: billingCountry,
      },
      {
        onSuccess: (data) => {
          setIsApplyingPromo(false);
          const promoCode = data.applied_promo?.code || data.code || trimmed;
          const promoDiscount = data.applied_promo?.discount_percent ?? data.discount_percent ?? 0;
          applyPromoCode(promoCode, promoDiscount);
          toast.success(data.message || `Promo code ${promoCode} applied!`);
          setPromoInput('');
          setPromoError('');
        },
        onError: (err: any) => {
          setIsApplyingPromo(false);
          setPromoError(err.userMessage || 'Invalid promo code');
        },
      },
    );
  };

  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const isSubmitting = checkoutMutation.isPending || isConfirmingPayment;

  const showPaymentFailure = (error: any) => {
    const message = error?.userMessage || error?.message || 'Payment could not be completed.';
    setFailedError({
      code: error?.code || 'ERR_PAYMENT_FAILED',
      subCode: error?.subCode || error?.sub_code,
      title: error?.errorTitle || error?.error_title,
      message,
    });
    toast.error(message);
    setIsFailedModalOpen(true);
  };

  const completeScaChallenge = async (clientSecret: string) => {
    setIsConfirmingPayment(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Secure payment confirmation is unavailable. Please try again.');
      }

      const confirmation = await stripe.confirmCardPayment(clientSecret);
      if (confirmation.error) {
        throw confirmation.error;
      }

      const paymentIntentStatus = confirmation.paymentIntent?.status;
      if (
        !confirmation.paymentIntent ||
        !paymentIntentStatus ||
        !['succeeded', 'processing'].includes(paymentIntentStatus)
      ) {
        throw new Error('Payment authentication was not completed. Please try again.');
      }

      let synced = await syncSubscriptionMutation.mutateAsync();
      if (!['active', 'trialing', 'processing'].includes(synced.status)) {
        // Stripe may update the subscription a moment after the PaymentIntent.
        await new Promise((resolve) => window.setTimeout(resolve, 300));
        synced = await syncSubscriptionMutation.mutateAsync();
      }

      if (synced.status === 'active' || synced.status === 'trialing') {
        recordLegalAck.mutate();
        removePromoCode();
        setStatus('active');
        setIsConfirmedModalOpen(true);
      } else if (synced.status === 'processing' || paymentIntentStatus === 'processing') {
        removePromoCode();
        toast.success('Your payment is processing.');
      } else {
        throw new Error('Payment was authenticated, but the subscription is not active yet.');
      }
    } catch (error: any) {
      showPaymentFailure(error);
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handleConfirmCheckout = async () => {
    if (isSubmitting) {
      return;
    }

    const activeBilling = activeBillingInfo || serverBillingProfile || billingInfo;
    if (!activeBilling?.fullName && !activeBilling?.companyName) {
      toast.error('Please add your billing information before continuing.');
      setIsBillingModalOpen(true);
      return;
    }

    const cards =
      activeCards.length > 0
        ? activeCards
        : serverPaymentMethods?.payment_methods || paymentMethods;
    if (cards.length === 0) {
      toast.error('Please add a payment method before continuing.');
      setIsPaymentModalOpen(true);
      return;
    }

    if (!isAgreed) {
      setAgreedError(true);
      toast.error('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }

    setAgreedError(false);

    const shouldFail =
      queryFail === 'true' ||
      searchParams.get('failed') === 'true' ||
      searchParams.get('payment') === 'failed';

    if (shouldFail) {
      setFailedError({
        code: 'ERR_PAYMENT_FAILED',
        message: 'Payment simulation failed. Please try another card.',
      });
      setIsFailedModalOpen(true);
      return;
    }

    const defaultCard = cards.find((c: any) => c.isDefault || c.is_default) || cards[0];

    checkoutMutation.mutate(
      {
        tier_slug: 'pro',
        interval: targetInterval,
        price_id: previewData?.price_id || proPlan?.price_id,
        payment_method_id: defaultCard?.id,
        promo_code: appliedPromo?.code,
        trial_choice: isTrial,
        trial_selection: isTrial,
        quote_id: previewData?.quote_id,
        country: billingCountry,
      },
      {
        onSuccess: (data) => {
          setIsFailedModalOpen(false);
          recordLegalAck.mutate();
          if (data.status === 'trialing' || data.status === 'active') {
            removePromoCode();
            setStatus('active');
            setIsConfirmedModalOpen(true);
          } else if (data.status === 'processing') {
            removePromoCode();
            toast.success('Your payment is processing.');
            setIsConfirmedModalOpen(true);
          } else {
            setFailedError({
              code: 'ERR_PAYMENT_FAILED',
              message: 'Payment could not be completed.',
            });
            setIsFailedModalOpen(true);
          }
        },
        onError: (err: any) => {
          const clientSecret = err?.fieldErrors?.payment_intent_client_secret;
          if (err?.code === 'ERR_PAYMENT_REQUIRES_ACTION' && clientSecret) {
            setIsFailedModalOpen(false);
            void completeScaChallenge(clientSecret);
            return;
          }

          showPaymentFailure(err);
        },
      },
    );
  };

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/plans');
    }
  };

  const handleNavigateLegal = (path: string) => {
    navigate(`/onboarding-trainer/dashboard/subscription/${path}`);
  };

  const activeBillingInfo = useMemo(() => {
    if (
      serverBillingProfile &&
      (serverBillingProfile.fullName ||
        serverBillingProfile.companyName ||
        serverBillingProfile.addressLine1)
    ) {
      return {
        billingType: serverBillingProfile.billingType || billingInfo?.billingType || 'individual',
        fullName: serverBillingProfile.fullName || '',
        companyName: serverBillingProfile.companyName || '',
        billingEmail: serverBillingProfile.billingEmail || '',
        country: serverBillingProfile.country || billingInfo?.country || '',
        stateRegion: serverBillingProfile.stateRegion || '',
        addressLine1: serverBillingProfile.addressLine1 || '',
        addressLine2: serverBillingProfile.addressLine2 || '',
        city: serverBillingProfile.city || '',
        postcode: serverBillingProfile.postcode || '',
        companyRegistrationNumber: serverBillingProfile.companyRegistrationNumber || '',
        taxVatNumber: serverBillingProfile.taxVatNumber || '',
      };
    }
    if (billingInfo?.fullName || billingInfo?.companyName || billingInfo?.addressLine1) {
      return billingInfo;
    }
    return null;
  }, [serverBillingProfile, billingInfo]);

  const hasBillingInfo = Boolean(
    activeBillingInfo?.fullName ||
    activeBillingInfo?.companyName ||
    activeBillingInfo?.addressLine1,
  );

  const activeCards: PaymentCard[] = useMemo(() => {
    const serverCards = serverPaymentMethods?.payment_methods;
    const rawCards = serverCards && serverCards.length > 0 ? serverCards : paymentMethods;

    if (!rawCards || rawCards.length === 0) {
      return [];
    }

    const defaultId = serverPaymentMethods?.default_payment_method_id;

    // Determine the single authoritative default card id:
    // 1. Explicit default_payment_method_id from server
    // 2. Or the first card that has is_default / isDefault set
    // 3. Or fallback to the first card in the list
    let singleDefaultId = defaultId;
    if (!singleDefaultId) {
      const foundDefault = rawCards.find((c: any) => c.is_default || c.isDefault);
      singleDefaultId = foundDefault ? foundDefault.id : rawCards[0]?.id;
    }

    const mapped: PaymentCard[] = rawCards.map((c: any) => {
      const isDefault = Boolean(singleDefaultId && c.id === singleDefaultId);
      const expiry =
        c.expiry ||
        (c.exp_month && c.exp_year
          ? `${String(c.exp_month).padStart(2, '0')} / ${String(c.exp_year).slice(-2)}`
          : '');

      return {
        id: c.id,
        brand: (c.brand?.toLowerCase() || 'visa') as CardBrand,
        last4: c.last4,
        expiry,
        cardholderName: c.cardholder_name || c.cardholderName || '',
        isDefault,
      };
    });

    return mapped.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
  }, [serverPaymentMethods, paymentMethods]);

  const [settingDefaultCardId, setSettingDefaultCardId] = useState<string | null>(null);

  const handleSelectCard = (cardId: string) => {
    if (settingDefaultCardId) return;

    if (serverPaymentMethods?.payment_methods?.some((c) => c.id === cardId)) {
      setSettingDefaultCardId(cardId);
      setDefaultPaymentMutation.mutate(cardId, {
        onSuccess: () => {
          useSubscriptionStore.getState().setDefaultPaymentMethod(cardId);
          toast.success('Default payment method updated.');
          void refetchPaymentMethods();
        },
        onError: () => {
          toast.error('Failed to update default card. Please try again.');
        },
        onSettled: () => {
          setSettingDefaultCardId(null);
        },
      });
    } else {
      useSubscriptionStore.getState().setDefaultPaymentMethod(cardId);
    }
  };

  if (isSummaryLoading) {
    return (
      <div
        className="checkout-page-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#E2F163' }} spin />} />
      </div>
    );
  }

  return (
    <div className="checkout-page-container">
      {/* Page Header */}
      <div className="checkout-page-header">
        <button type="button" className="checkout-back-btn" onClick={handleBack}>
          <UserChevronLeftSVG />
          <span>{'Checkout'}</span>
        </button>
        <p className="checkout-header-subtitle">{"You're one step away from unlocking Pro."}</p>
      </div>

      {/* Checkout 2-Column Grid */}
      <div className="checkout-content-grid">
        {/* Left Column: Summary, Info, Payment */}
        <div className="checkout-left-col">
          {/* Card 1: Billing Summary */}
          <div className="checkout-card billing-summary-card">
            <h3>{'Billing Summary'}</h3>

            {isPreviewError || !previewData ? (
              <div
                className="summary-error-state"
                style={{ padding: '24px 0', textAlign: 'center' }}
              >
                <p style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>
                  {(previewError as any)?.userMessage || 'Unable to calculate checkout summary.'}
                </p>
                <button
                  type="button"
                  className="btn-retry-preview"
                  onClick={() => refetchPreview()}
                  style={{
                    background: 'transparent',
                    border: '1px solid #e2f163',
                    color: '#e2f163',
                    borderRadius: 4,
                    padding: '6px 16px',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {'Retry'}
                </button>
              </div>
            ) : (
              <div className="summary-rows">
                {effectiveTrial ? (
                  <>
                    <div className="summary-row due-today-row">
                      <span className="row-label">{'Due today'}</span>
                      <span className="row-value bold-val">{previewData.formatted_due_today}</span>
                    </div>
                    <div className="summary-row">
                      <span className="row-label">{'Billing Cycle'}</span>
                      <span className="row-value capitalize">{billingCycle}</span>
                    </div>
                    <div className="summary-row">
                      <span className="row-label">{'Trial ends'}</span>
                      <span className="row-value">{trialEndsDateFormatted}</span>
                    </div>
                    <div className="summary-divider" />
                    <div className="summary-row">
                      <span className="row-label">{proPlan?.name || '—'}</span>
                      <span className="row-value">
                        {previewData.formatted_discount ? (
                          <>
                            <span className="strikethrough">{previewData.formatted_subtotal}</span>{' '}
                            {previewData.formatted_discounted_subtotal}
                          </>
                        ) : (
                          previewData.formatted_subtotal
                        )}
                      </span>
                    </div>
                    {previewData.formatted_discount && (
                      <div className="summary-row discount-row">
                        <span className="row-label">
                          {previewData.promotion_percentage
                            ? `Discount (${previewData.promotion_percentage}% off)`
                            : 'Discount'}
                        </span>
                        <span className="row-value discount-val">
                          -{previewData.formatted_discount}
                        </span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span className="row-label">
                        {`${previewData.tax_display_name || 'Tax'} (${previewData.tax_rate_percent}%)`}
                      </span>
                      <span className="row-value">{previewData.formatted_tax}</span>
                    </div>
                    <div className="summary-row total-row">
                      <span className="row-label">{'First payment after trial'}</span>
                      <span className="row-value highlight-val">
                        {previewData.formatted_first_payment_after_trial ||
                          previewData.formatted_total}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="summary-row">
                      <span className="row-label">{proPlan?.name || '—'}</span>
                      <span className="row-value">
                        {previewData.formatted_discount ? (
                          <>
                            <span className="strikethrough">{previewData.formatted_subtotal}</span>{' '}
                            {previewData.formatted_discounted_subtotal}
                          </>
                        ) : (
                          previewData.formatted_subtotal
                        )}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="row-label">{'Billing Cycle'}</span>
                      <span className="row-value capitalize">{billingCycle}</span>
                    </div>
                    <div className="summary-divider" />
                    <div className="summary-row">
                      <span className="row-label">{'Subtotal'}</span>
                      <span className="row-value">{previewData.formatted_subtotal}</span>
                    </div>
                    {previewData.formatted_discount && (
                      <div className="summary-row discount-row">
                        <span className="row-label">
                          {previewData.promotion_percentage
                            ? `Discount (${previewData.promotion_percentage}% off)`
                            : 'Discount'}
                        </span>
                        <span className="row-value discount-val">
                          -{previewData.formatted_discount}
                        </span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span className="row-label">
                        {previewData.is_tax_inclusive
                          ? `${previewData.tax_display_name || 'Tax'} (${previewData.tax_rate_percent}%)`
                          : `${previewData.tax_display_name || 'Tax'} (${previewData.tax_rate_percent}%)`}
                      </span>
                      <span className="row-value">{previewData.formatted_tax}</span>
                    </div>
                    <div className="summary-row total-row">
                      <span className="row-label">{'Total'}</span>
                      <span className="row-value highlight-val">{previewData.formatted_total}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Add Promo Code */}
            <div className="promo-code-section">
              {appliedPromo && previewData?.applied_promotion_code ? (
                <div className="applied-promo-badge">
                  <span>
                    {previewData.promotion_percentage
                      ? `Promo applied: ${previewData.applied_promotion_code} (-${previewData.promotion_percentage}%)`
                      : `Promo applied: ${previewData.applied_promotion_code} (-${previewData.formatted_promotion_discount_amount || previewData.formatted_discount || ''})`}
                  </span>
                  <button type="button" onClick={removePromoCode} aria-label="Remove promo code">
                    {'✕'}
                  </button>
                </div>
              ) : appliedPromo ? (
                <div className="applied-promo-badge">
                  <span>{`Promo applied: ${appliedPromo.code}`}</span>
                  <button type="button" onClick={removePromoCode} aria-label="Remove promo code">
                    {'✕'}
                  </button>
                </div>
              ) : (
                <>
                  <label htmlFor="promo-input">{'Add promo code'}</label>
                  <form
                    className="promo-input-row"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleApplyPromo();
                    }}
                  >
                    <div className="promo-input-field-wrap">
                      <input
                        id="promo-input"
                        type="text"
                        placeholder="Code"
                        maxLength={30}
                        className={promoError ? 'has-error' : ''}
                        value={promoInput}
                        onChange={(e) => {
                          setPromoInput(e.target.value.toUpperCase());
                          if (promoError) setPromoError('');
                        }}
                        onBlur={(e) => setPromoInput(e.target.value.trim())}
                        autoComplete="off"
                      />
                      {promoInput.length > 0 && (
                        <button
                          type="button"
                          className="clear-promo-btn"
                          onClick={() => {
                            setPromoInput('');
                            if (promoError) setPromoError('');
                          }}
                          aria-label="Clear promo code"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <button type="submit" className="btn-add-promo" disabled={isApplyingPromo}>
                      {isApplyingPromo ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 13, color: '#060723' }} spin />
                          }
                        />
                      ) : (
                        'Add'
                      )}
                    </button>
                  </form>
                  {promoError && <p className="promo-error-text">{promoError}</p>}
                </>
              )}
            </div>
          </div>

          {/* Card 2: Billing Information */}
          <div className="checkout-card billing-info-card">
            <div className="card-header-with-action">
              <h3>{'Billing Information'}</h3>
              {hasBillingInfo ? (
                <button
                  type="button"
                  className="icon-action-btn"
                  onClick={() => setIsBillingModalOpen(true)}
                  title="Edit billing info"
                >
                  <BillingInfoEditSVG />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-add-mini desktop-only-add"
                  onClick={() => setIsBillingModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              )}
            </div>

            {hasBillingInfo && activeBillingInfo ? (
              <div className="info-key-value-list">
                <div className="kv-item">
                  <span className="kv-key">
                    {activeBillingInfo.billingType === 'business' ? 'Company Name' : 'Full Name'}
                  </span>
                  <span className="kv-val">
                    {activeBillingInfo.fullName || activeBillingInfo.companyName}
                  </span>
                </div>
                <div className="kv-item">
                  <span className="kv-key">{'Billing Email'}</span>
                  <span className="kv-val">{activeBillingInfo.billingEmail}</span>
                </div>
                <div className="kv-item">
                  <span className="kv-key">{'Address'}</span>
                  <span className="kv-val">
                    {activeBillingInfo.addressLine1}
                    {activeBillingInfo.city ? `, ${activeBillingInfo.city}` : ''}
                    {activeBillingInfo.postcode ? ` ${activeBillingInfo.postcode}` : ''}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <p className="card-empty-description">
                  {'These details will appear on your invoices.'}
                </p>
                <button
                  type="button"
                  className="btn-add-full mobile-only-add"
                  onClick={() => setIsBillingModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              </>
            )}
          </div>

          {/* Card 3: Payment Method */}
          <div className="checkout-card payment-method-card">
            <div className="card-header-with-action">
              <h3>{'Payment Method'}</h3>
              {activeCards.length > 0 ? (
                <button
                  type="button"
                  className="btn-add-mini"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-add-mini desktop-only-add"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              )}
            </div>

            {activeCards.length > 0 ? (
              <div className="saved-cards-list">
                {activeCards.slice(0, 3).map((card) => {
                  const isSettingThisCard = settingDefaultCardId === card.id;
                  const isBusy = Boolean(settingDefaultCardId);
                  return (
                    <div
                      key={card.id}
                      className={`payment-card-row ${card.isDefault ? 'selected-card' : ''} ${isSettingThisCard ? 'is-loading' : ''} ${isBusy ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isBusy) return;
                        handleSelectCard(card.id);
                      }}
                    >
                      <div className="card-brand-wrap">
                        <CardBrandLogo brand={card.brand} />
                      </div>
                      <div className="card-info-text">
                        <span className="card-title-text">
                          {getCardBrandDisplayName(card.brand)}
                        </span>
                        <span className="card-mask">
                          {formatMaskedCardNumber(card.brand, card.last4)}
                        </span>
                      </div>
                      {isSettingThisCard ? (
                        <span className="setting-default-indicator">
                          <Spin
                            indicator={
                              <LoadingOutlined style={{ fontSize: 13, color: '#ffa100' }} spin />
                            }
                          />
                          <span>{'Updating...'}</span>
                        </span>
                      ) : card.isDefault ? (
                        <span className="default-pill">{'Default'}</span>
                      ) : null}
                    </div>
                  );
                })}

                {activeCards.length > 3 && (
                  <button
                    type="button"
                    className="btn-view-more-cards"
                    onClick={() => setIsSelectCardModalOpen(true)}
                  >
                    {`+${activeCards.length - 3} more cards`}
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="card-empty-description">
                  {'A payment method is required to start the trial. You won’t be charged today.'}
                </p>
                <button
                  type="button"
                  className="btn-add-full mobile-only-add"
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  <AddButtonSVG />
                  <span>{'Add'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right Column: Pro Plan Card & CTA */}
        <div className="checkout-right-col">
          <div className="pro-plan-review-card">
            <div className="pro-card-header">
              <div className="title-row">
                <h3>{proPlan?.name}</h3>
                {(proPlan?.recommended || proPlan?.tier_slug === 'pro') && (
                  <span className="recommended-badge">
                    <RecommendedStarSVG />
                    <span>{'Recommended'}</span>
                  </span>
                )}
              </div>
              <div className="price-row">
                <span className="price-amount">{proPlan?.formatted_amount}</span>
                <span className="price-period">{proPlan?.billing_period}</span>
              </div>
              <p className="price-subtext">
                {effectiveTrial && proPlan?.trial_information?.text
                  ? proPlan.trial_information.text
                  : proPlan?.description}
              </p>
            </div>

            <div className="features-list">
              {featuresList.map((feat) => (
                <div className="feature-item" key={feat}>
                  <CheckmarkSVG className="gold-check" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            {/* Trial Callout Box */}
            {effectiveTrial && (
              <div className="trial-callout-box">
                <div className="callout-icon">
                  <TrialIncludedSVG />
                </div>
                <div className="callout-text">
                  <h4>{`${trialDays}-day free trial included`}</h4>
                  <p>
                    {`No charge today. After the trial, you’ll be charged ${
                      previewData?.formatted_first_payment_after_trial || '—'
                    } unless you cancel before the trial ends.`}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Security note */}
          <div className="security-notice">
            <div className="security-icon-wrap">
              <SecurePaymentLockSVG />
            </div>
            <span>{'Your payment information is secure and encrypted.'}</span>
          </div>

          {/* Legal agreement checkbox */}
          <label className={`terms-agreement-checkbox ${agreedError ? 'has-error' : ''}`}>
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={(e) => {
                setIsAgreed(e.target.checked);
                if (e.target.checked) setAgreedError(false);
              }}
            />
            <span>
              {'I agree to the '}
              <button
                type="button"
                className="legal-inline-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateLegal('terms-of-service');
                }}
              >
                {'Terms of Service'}
              </button>
              {' and '}
              <button
                type="button"
                className="legal-inline-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateLegal('privacy-policy');
                }}
              >
                {'Privacy Policy'}
              </button>
              {'.'}
            </span>
          </label>

          {/* Submit Button */}
          <button
            type="button"
            className="btn-confirm-checkout"
            onClick={handleConfirmCheckout}
            disabled={isSubmitting || isSummaryLoading || Boolean(isPreviewError) || !previewData}
          >
            {isSubmitting ? (
              <>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 18, color: '#060723' }} spin />}
                />
                <span>{'Processing...'}</span>
              </>
            ) : (
              <span>
                {effectiveTrial ? `Start ${trialDays}-day free trial` : 'Confirm Upgrade to Pro'}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Modals */}
      <BillingInfoModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        onSuccess={() => {
          void refetchBillingProfile();
        }}
      />

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={() => {
          void refetchPaymentMethods();
        }}
      />

      <SelectPaymentMethodModal
        isOpen={isSelectCardModalOpen}
        onClose={() => setIsSelectCardModalOpen(false)}
        cards={activeCards}
        isUpdatingCardId={settingDefaultCardId}
        onSelectCard={handleSelectCard}
        onAddNewCard={() => setIsPaymentModalOpen(true)}
      />

      <PaymentConfirmedModal
        isOpen={isConfirmedModalOpen}
        onClose={() => setIsConfirmedModalOpen(false)}
        isTrialMode={effectiveTrial}
        amountFormatted={previewData?.formatted_total}
        dueTodayFormatted={previewData?.formatted_due_today}
        firstPaymentFormatted={
          previewData?.formatted_first_payment_after_trial || previewData?.formatted_total
        }
        trialEndsDateFormatted={trialEndsDateFormatted}
      />

      <PaymentFailedModal
        isOpen={isFailedModalOpen}
        onClose={() => setIsFailedModalOpen(false)}
        onTryAgain={handleConfirmCheckout}
        onChangePaymentMethod={() => {
          setIsFailedModalOpen(false);
          setIsPaymentModalOpen(true);
        }}
        errorCode={failedError.code}
        subCode={failedError.subCode}
        errorTitle={failedError.title}
        errorMessage={failedError.message}
        isLoading={isSubmitting}
      />
    </div>
  );
};
